import React, { createContext, useContext, useState, ReactNode, useMemo } from 'react';
import { createTheme, Theme } from '@mui/material/styles';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
  theme: Theme;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const getTheme = (mode: ThemeMode): Theme =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: '#0B4F8A',
        light: '#1976D2',
        dark: '#083968',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#00897B',
        light: '#26A69A',
        dark: '#005F55',
        contrastText: '#ffffff',
      },
      error: { main: '#D32F2F' },
      warning: { main: '#F57C00' },
      success: { main: '#2E7D32' },
      background: mode === 'light'
        ? { default: '#F8FAFC', paper: '#FFFFFF' }
        : { default: '#0D1117', paper: '#161B22' },
      text: mode === 'light'
        ? { primary: '#1A2332', secondary: '#4A5568' }
        : { primary: '#E6EDF3', secondary: '#8B949E' },
    },
    typography: {
      fontFamily: '"Source Sans 3", "Noto Sans", system-ui, sans-serif',
      h1: { fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 700 },
      h2: { fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 700 },
      h3: { fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 600 },
      h4: { fontWeight: 700 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    shape: { borderRadius: 8 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            padding: '10px 24px',
            fontSize: '0.95rem',
            fontWeight: 600,
          },
          containedPrimary: {
            boxShadow: '0 2px 8px rgba(11, 79, 138, 0.3)',
            '&:hover': { boxShadow: '0 4px 16px rgba(11, 79, 138, 0.4)' },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: mode === 'light'
              ? '0 1px 4px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)'
              : '0 1px 4px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.2)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 6, fontWeight: 600 },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: mode === 'light'
              ? '0 1px 3px rgba(0,0,0,0.1)'
              : '0 1px 3px rgba(0,0,0,0.4)',
          },
        },
      },
    },
  });

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>('light');

  const toggleTheme = () => setMode(prev => prev === 'light' ? 'dark' : 'light');

  const theme = useMemo(() => getTheme(mode), [mode]);

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, theme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeContext = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useThemeContext must be used within ThemeProvider');
  return context;
};
